import { Check, ListChecks, StickyNote } from "lucide-react";
import type { TaskRowProps } from "../types";
import { PriorityIcon, StatusIcon } from "@/components/icons";
import { CarriedLabel, DeadlineLabel, RepeatBadge, RowHoverActions, SnoozeBadge, SyncBadge, TodayStar } from "@/components/task-bits";
import { cn } from "@/lib/utils";

/** Round checkbox: a thin ring that fills with ink and a check. */
export function FocusCheckbox({
  checked,
  cancelled,
  onToggle,
  label,
  size = 16,
}: {
  checked: boolean;
  cancelled?: boolean;
  onToggle: () => void;
  label: string;
  size?: number;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      onKeyDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      style={{ width: size, height: size, borderRadius: Math.round(size * 0.26) }}
      className={cn(
        "group/cb relative inline-flex shrink-0 items-center justify-center border-[1.5px] transition-[background-color,border-color,transform] duration-150 active:scale-90",
        checked
          ? cancelled
            ? "border-subtle bg-subtle text-background"
            : "border-foreground/80 bg-foreground/80 text-background"
          : "border-foreground/30 hover:border-foreground/60",
      )}
    >
      <Check
        strokeWidth={3.25}
        style={{ width: size * 0.62, height: size * 0.62 }}
        className={cn("transition-opacity duration-150", checked ? "opacity-100" : "text-foreground/45 opacity-0 group-hover/cb:opacity-100")}
      />
    </button>
  );
}

/** Balanced-density row (38px): checkbox, status glyph, one-line title + location, quiet metadata on the right. */
export function FocusTaskRow(p: TaskRowProps) {
  const t = p.task;
  const closed = t.status === "done" || t.status === "cancelled";
  const doneItems = t.checklist.filter((c) => c.done).length;
  return (
    <div
      {...p.rowProps}
      onClick={(e) => {
        p.rowProps.onClick?.(e);
        p.onSelect();
      }}
      onDoubleClick={p.onOpen}
      className={cn(
        "group/row relative flex h-[38px] cursor-default items-center gap-3 rounded-lg px-2.5 outline-none transition-colors duration-100",
        p.selected || p.expanded ? "bg-selected" : "hover:bg-hover",
        "focus-visible:ring-2 focus-visible:ring-ring/40",
      )}
    >
      <FocusCheckbox
        checked={closed}
        cancelled={t.status === "cancelled"}
        onToggle={p.onToggleComplete}
        label={closed ? `Reopen ${t.title}` : `Complete ${t.title}`}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation();
          p.onSelect();
          p.onOpen();
        }}
        className="flex min-w-0 flex-1 cursor-default items-center gap-2 text-left"
      >
        {!closed && (t.status === "in_progress" || t.status === "backlog") && <StatusIcon status={t.status} className="size-3.5 shrink-0" />}
        {p.plannedToday && !closed && <TodayStar className="size-3 shrink-0" />}
        <span
          title={t.title}
          className={cn(
            "min-w-0 shrink truncate text-[14px] leading-5",
            closed ? "text-muted-foreground line-through decoration-foreground/25" : "text-foreground",
          )}
        >
          {t.title}
        </span>
        {p.location && <span className="min-w-[2.5rem] max-w-[40%] shrink-[12] truncate text-[12.5px] text-subtle">{p.location}</span>}
        {!closed && t.checklist.length > 0 && (
          <span className="inline-flex shrink-0 items-center gap-1 text-[12px] text-subtle tabular" aria-label={`Checklist ${doneItems} of ${t.checklist.length}`}>
            <ListChecks className="size-3" aria-hidden />
            {doneItems}/{t.checklist.length}
          </span>
        )}
        {!closed && t.description && <StickyNote className="size-3 shrink-0 text-subtle" aria-label="Has notes" />}
      </button>
      <div className="flex shrink-0 items-center gap-2.5 text-[12.5px] text-muted-foreground">
        <SyncBadge mark={p.sync} />
        <SnoozeBadge task={t} timezone={p.timezone} />
        <RepeatBadge task={t} />
        {p.carriedFrom && <CarriedLabel from={p.carriedFrom} today={p.today} />}
        {t.labels.length > 0 && !closed && (
          <span className="hidden items-center gap-1 xl:inline-flex">
            {t.labels.slice(0, 2).map((l) => (
              <span key={l} className="rounded-md bg-foreground/[0.055] px-1.5 text-[11.5px] leading-[18px] text-muted-foreground">
                {l}
              </span>
            ))}
          </span>
        )}
        {t.deadline && !closed && <DeadlineLabel deadline={t.deadline} today={p.today} />}
        <span className="inline-flex w-4 justify-center">{t.priority !== "none" && <PriorityIcon priority={t.priority} className="size-3.5" />}</span>
        {t.readableId !== "DSK-0" && (
          <span className="hidden min-w-[52px] whitespace-nowrap text-right text-[12px] text-subtle tabular md:inline">{t.readableId}</span>
        )}
        {p.trailing}
        <span
          className={cn(
            "-mr-1 flex w-6 justify-center transition-opacity",
            p.selected ? "opacity-100" : "opacity-0 focus-within:opacity-100 group-hover/row:opacity-100",
          )}
        >
          {p.menu}
        </span>
      </div>
      <RowHoverActions selected={p.selected || p.expanded} className="right-[32px] top-[7px] h-6">
        {p.quickActions}
      </RowHoverActions>
    </div>
  );
}
